/**
 * Dead weight — items unworn in 90+ days, with wasted-rupee summary.
 */
import { store } from '../store.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';
import type { ClothingItem } from '../types.ts';

const PAGE = 20;

export class AlmariDeadWeight extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #all: ClothingItem[] = [];
  #showing = PAGE;
  #deadValue = 0;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) {
        this.#deadValue = s.summary.insights.dead_weight_value_inr;
        const cutoff = ninetyDaysAgo();
        this.#all = s.summary.items.filter((it) => !it.last_worn || it.last_worn < cutoff);
        this.#render();
      }
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(): void {
    if (!this.#all.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }
    const visible = this.#all.slice(0, this.#showing);
    const more = this.#all.length - visible.length;
    const rows = visible.map((it) => /* html */ `
<div class="tx">
  <div class="left">
    <div class="m">${escape(it.name)}</div>
    <div class="i">${escape(it.brand)} · ${CATEGORY_DISPLAY[it.category] ?? it.category}</div>
  </div>
  <div class="right">
    <div class="amt">${formatINR(it.value_inr)}</div>
    <div class="d">${it.last_worn ? `${daysAgo(it.last_worn)}d ago` : 'never worn'}</div>
  </div>
</div>`).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; }
.title {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 6px;
  padding-left: 4px;
}
.subtitle {
  font-family: var(--display);
  font-style: italic;
  font-size: 14px;
  color: var(--ink-dim);
  margin-bottom: 12px;
  padding-left: 4px;
}
.subtitle b { font-weight: 700; color: var(--rose); font-style: normal; font-feature-settings: "tnum" 1; }
.list { display: grid; gap: 0; background: var(--paper-card); border: 1px solid var(--rose); border-radius: 8px; overflow: hidden; }
.tx {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  align-items: center;
}
.tx:last-child { border-bottom: none; }
.m {
  font-family: var(--display);
  font-size: 17px;
  color: var(--ink);
  font-weight: 700;
  letter-spacing: -0.005em;
}
.i {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  margin-top: 3px;
  text-transform: lowercase;
  max-width: 56vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.right { text-align: right; }
.amt {
  font-family: var(--display);
  font-size: 17px;
  font-weight: 700;
  color: var(--ink);
  font-feature-settings: "tnum" 1;
}
.d {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--rose);
  margin-top: 3px;
}
button {
  margin-top: 14px;
  width: 100%;
  padding: 14px;
  background: transparent;
  border: 1px solid var(--rule);
  border-radius: 8px;
  font-family: var(--mono);
  font-size: 11px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-dim);
}
button:active { background: var(--paper-up); }
</style>
<div class="title">Dead weight</div>
<div class="subtitle"><b>${formatINR(this.#deadValue)}</b> ka kapda · ${this.#all.length} piece${this.#all.length === 1 ? '' : 's'} · 90+ days unworn</div>
<div class="list">${rows}</div>
${more > 0 ? `<button id="more">+ ${more} more</button>` : ''}
`;
    const moreBtn = this.#shadow.getElementById('more');
    if (moreBtn) moreBtn.addEventListener('click', () => { this.#showing += PAGE; this.#render(); });
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function ninetyDaysAgo(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 90);
  return d.toISOString().slice(0, 10);
}

function daysAgo(iso: string): number {
  const then = new Date(iso + 'T00:00:00Z');
  if (isNaN(then.getTime())) return 0;
  const now = new Date();
  return Math.max(0, Math.round((now.getTime() - then.getTime()) / 86_400_000));
}

customElements.define('almari-dead-weight', AlmariDeadWeight);
